import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { CostLineType } from '@/features/dashboard/bookings/types/BookingBillDto.ts';
import {
  negateMoneyList,
  sumMoney,
} from '@/features/dashboard/bookingChanges/utils/money.ts';

/**
 * Wpływ otwartej paczki na kwoty, w postaci, w której ekran go pyta.
 *
 * Front kwot **nie liczy** — wszystko poniżej przychodzi policzone
 * z `GET …/changes/{changeId}/preview`. Ten model jest samym indeksem nad tą
 * odpowiedzią, żeby wiersz, osoba i cała paczka pytały o deltę w jednym
 * miejscu, zamiast przeszukiwać listę w każdym mapperze osobno.
 */

/** Delta grupy rozliczeniowej całej rezerwacji — pozycje bez uczestnika. */
export const BOOKING_WIDE_KEY = 'booking';

/** Zmiana kwoty jednej pozycji księgi. */
export interface ChangePreviewLine {
  delta: MoneyValueDto[];
  /** Strona „po” jest pusta — pozycja znika z rachunku. */
  removed: boolean;
}

export interface ChangePreview {
  /** Kwota całej paczki, per waluta. */
  total: MoneyValueDto[];
  /** Klucz: `participantId` albo `BOOKING_WIDE_KEY`. */
  byParticipant: Record<string, MoneyValueDto[]>;
  /** Zmienione pozycje księgi, po `current.id` — zwykłe złączenie z rachunkiem. */
  changedLines: Record<string, ChangePreviewLine>;
  /**
   * Pozycje, które paczka dopiero doda — zebrane po `costRowKey`, bo strona
   * „przed” jest pusta i nie ma czego złączyć.
   */
  addedRows: Record<string, MoneyValueDto[][]>;
}

/**
 * Tożsamość wiersza rachunku odtworzona z pozycji księgi.
 *
 * Wiersz, który paczka **zmienia**, wiąże się po `current.id` i klucza nie
 * potrzebuje. Klucz jest wyłącznie dla pozycji, którą paczka **dodaje**:
 * kontrakt nie daje jej żadnego identyfikatora (`current` jest `null`, `target`
 * nie ma `id`), a wiersz („Warsztaty”, „Transport”) stoi już na ekranie i to on
 * ma pokazać nową kwotę.
 *
 * Klucz jest celowo grubszy od `CostLineKey` backendu — wariantu nie
 * rozróżnia, bo wiersz wielowartościowy zbiera wszystkie warianty swojej opcji.
 */
export const costRowKey = (
  participantId: string | null,
  type: CostLineType,
  discriminator: string | null = null,
): string =>
  [participantId ?? BOOKING_WIDE_KEY, type, discriminator ?? ''].join('|');

/**
 * Delta osoby (`null` = pozycje całej rezerwacji), po jednej pozycji na walutę.
 * Pusta lista znaczy „paczka jej nie rusza”.
 */
export const previewParticipantDelta = (
  preview: ChangePreview | null,
  participantId: string | null,
): MoneyValueDto[] =>
  preview?.byParticipant[participantId ?? BOOKING_WIDE_KEY] ?? [];

/**
 * Delta uczestnika bez jego udziałów w pozycjach ręcznych całej rezerwacji
 * (po ich opisach).
 *
 * Backend rozkłada zniżkę całej rezerwacji na uczestników, więc ich suma
 * z podglądu już zawiera te udziały — a ekrany pokazują je w wierszu całej
 * rezerwacji. Bez odjęcia kwota liczyłaby się dwa razy. Uczestnik z własną
 * pozycją ręczną o tym samym opisie jest nierozróżnialny i traci ją razem
 * z udziałem — klucz wiersza ręcznego nie ma nic poza opisem.
 */
export const previewOwnParticipantDelta = (
  preview: ChangePreview | null,
  participantId: string,
  bookingWideDescriptions: string[],
): MoneyValueDto[] => {
  const delta = previewParticipantDelta(preview, participantId);
  const shares = [...new Set(bookingWideDescriptions)].flatMap(
    (description) =>
      preview?.addedRows[
        costRowKey(participantId, CostLineType.MANUAL, description)
      ]?.flat() ?? [],
  );
  return shares.length === 0
    ? delta
    : sumMoney([...delta, ...negateMoneyList(shares)]);
};

/** Wpływ paczki na jeden wiersz rachunku — wiersz bywa kilkoma pozycjami księgi. */
export interface ChangePreviewRowImpact {
  /** Po jednej pozycji na walutę; pusta lista = wiersz bez zmiany kwoty. */
  delta: MoneyValueDto[];
  /** Ile pozycji księgi wiersz traci. */
  removed: number;
  /** Ile pozycji księgi wiersz zyskuje. */
  added: number;
}

const NO_IMPACT: ChangePreviewRowImpact = {
  delta: [],
  removed: 0,
  added: 0,
};

/**
 * Wpływ paczki na wiersz: złączenie po identyfikatorach jego pozycji księgi
 * plus pozycje dopisywane, które identyfikatora nie mają.
 *
 * Sumowanie po pozycjach jest tu jedyną arytmetyką, jakiej backend wyręczyć nie
 * może: „jeden wiersz na opcję” to układ ekranu, a księga trzyma osobną pozycję
 * na wariant — tak samo jak przy sumie sekcji.
 */
export const previewRowImpact = (
  preview: ChangePreview | null,
  costLineIds: string[],
  rowKey: string | null,
): ChangePreviewRowImpact => {
  if (!preview) {
    return NO_IMPACT;
  }

  const changed = costLineIds
    .map((id) => preview.changedLines[id])
    .filter((line): line is ChangePreviewLine => !!line);
  const added = (rowKey && preview.addedRows[rowKey]) || [];

  return {
    delta: sumMoney([...changed.map((line) => line.delta), ...added].flat()),
    removed: changed.length,
    added: added.length + changed.filter((line) => !line.removed).length,
  };
};
